interface UseSliderOptions {
  itemsCount: number | Ref<number>
  itemsPerView?: number | Ref<number>
  gap?: number
}

export function useSlider(options: UseSliderOptions) {
  const { itemsCount, itemsPerView = 1, gap = 24 } = options

  const currentIndex = ref(0)
  const sliderRef = ref<HTMLElement | null>(null)
  const slideWidth = ref(0)

  const itemsCountValue = computed(() => unref(itemsCount))
  const itemsPerViewValue = computed(() => unref(itemsPerView))

  const totalPages = computed(() => 
    Math.ceil(itemsCountValue.value / itemsPerViewValue.value)
  )
  
  const maxIndex = computed(() => Math.max(0, totalPages.value - 1))

  const updateSlideWidth = () => {
    if (sliderRef.value) {
      slideWidth.value = sliderRef.value.offsetWidth + gap
    }
  }

  const prevSlide = () => {
    if (currentIndex.value > 0) currentIndex.value--
  }

  const nextSlide = () => {
    if (currentIndex.value < maxIndex.value) currentIndex.value++
  }

  const goToSlide = (index: number) => {
    currentIndex.value = Math.max(0, Math.min(index, maxIndex.value))
  }

  const trackStyle = computed(() => ({
    transform: `translateX(-${currentIndex.value * slideWidth.value}px)`
  }))

  const isFirstSlide = computed(() => currentIndex.value === 0)
  const isLastSlide = computed(() => currentIndex.value >= maxIndex.value)

  // Вызываем updateSlideWidth когда sliderRef становится доступным (важно для v-if/v-else)
  watch(sliderRef, (el) => {
    if (el) {
      nextTick(() => updateSlideWidth())
    }
  }, { immediate: true })

  onMounted(() => {
    updateSlideWidth()
    window.addEventListener('resize', updateSlideWidth)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', updateSlideWidth)
  })

  // Reset to first slide if items change
  watch(itemsCountValue, () => {
    if (currentIndex.value > maxIndex.value) {
      currentIndex.value = maxIndex.value
    }
  })

  return {
    currentIndex,
    sliderRef,
    slideWidth,
    totalPages,
    maxIndex,
    trackStyle,
    isFirstSlide,
    isLastSlide,
    prevSlide,
    nextSlide,
    goToSlide,
    updateSlideWidth
  }
}

